import { Box, Tooltip, alpha } from "@mui/material";
import { getColorFilter, getCountryInfo } from "../../utils/mapUtils";
import { SIDEBAR_GAP, SIDEBAR_WIDTH } from "./sidebarWidths";
import { CLOCK_THEMES } from "./MapClock";
import { glassSx } from "./overlayStyles";
import { MONO } from "./LocationStatusBar";

const IMPORTANT_RED = "#FF3B3B";
// Outpost | Total | Active | Inactive (same columns for the header and every row)
const OUTPOST_COLUMNS = "1fr 48px 40px 48px";

const TargetsState = ({
  targets,
  isOpen,
  sidebarWidth = SIDEBAR_WIDTH,   // width of the sidebar that is open
  foeFilters,
  mode = "live",                  // 'live' (Radar) | 'replay' (Review)
}) => {
  const { accent } = CLOCK_THEMES[mode] || CLOCK_THEMES.live;

  const INACTIVE_TIMEOUT_MS = 5 * 60 * 1000;
  const now = Date.now();

  const isActive = (target) =>
    now - new Date(target.updatedAt).getTime() <= INACTIVE_TIMEOUT_MS;

  // 1. Convert targets map/object into a flat array
  const rawTargets = Array.isArray(targets)
    ? targets
    : Object.values(targets).flat();

  // 2. Filter out merged targets (discounting them from all totals)
  const safeTargets = rawTargets.filter(target => target && target.merged !== true);

  const totalActive = safeTargets.filter(isActive).length;
  const totalInactive = safeTargets.length - totalActive;

  let countryInfo = "xx";

  const foeSummary = safeTargets.reduce(
    (acc, target) => {
      switch (target.foe) {
        case 20:
          acc.enemy++;
          break;

        case 21:
          acc.friendly++;
          break;

        case 22:
          acc.pass++;
          break;

        case 23:
        default:
          acc.unknown++;
          break;
      }

      if (target.set_off === 0x55)
        acc.important++;

      countryInfo = getCountryInfo(target.address);
      if(target.address > 0 && countryInfo.iso === "tz")
        acc.friendly++;

      return acc;
    },
    {
      enemy: 0,
      friendly: 0,
      pass: 0,
      unknown: 0,
      important: 0,
    }
  );

  // 3. Discount merged targets from individual Outpost counts as well
  const countsByOutpost = Object.entries(targets).reduce((acc, [outpostNum, arr]) => {
    // Filter out merged targets from this specific outpost's array
    const filteredArr = arr.filter(target => target && target.merged !== true);
    const active = filteredArr.filter(isActive).length;

    acc[outpostNum] = {
      total: filteredArr.length,
      active,
      inactive: filteredArr.length - active,
    };

    return acc;
  }, {});

  if (safeTargets.length === 0) return null;

  const muted = alpha("#fff", 0.6);

  // right: cancel the letter-spacing after the last letter so the label's right edge lines up
  // exactly with the right-aligned numbers below it
  const sectionLabel = (text, right = false) => (
    <Box
      sx={{
        fontSize: "0.58rem",
        fontWeight: 800,
        letterSpacing: "0.16em",
        color: muted,
        ...(right && { textAlign: "right", mr: "-0.16em" }),
      }}
    >
      {text}
    </Box>
  );

  const foeItems = [
    { key: "enemy", label: "Enemy", code: 20 },
    { key: "friendly", label: "Friendly", code: 21 },
    { key: "pass", label: "Pass", code: 22 },
    { key: "unknown", label: "Unknown", code: 23 },
  ];

  const outposts = Object.entries(countsByOutpost).sort((a, b) => Number(a[0]) - Number(b[0]));

  return (
    <Box
      position="absolute"
      bottom={12}
      right={isOpen ? sidebarWidth + SIDEBAR_GAP + 1 : 12}
      zIndex={1300}
      sx={{
        width: 230,
        px: 1.25,
        py: 1,
        borderRadius: "12px",
        color: "#fff",
        fontSize: "0.78rem",
        userSelect: "none",
        ...glassSx(accent),
      }}
    >
      {/* Header: total, with active · inactive */}
      <Box display="flex" alignItems="flex-end" justifyContent="space-between" mb={0.75}>
        <Box>
          {sectionLabel("TARGETS")}
          <Box sx={{ fontFamily: MONO, fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.1, color: accent, fontVariantNumeric: "tabular-nums" }}>
            {safeTargets.length}
          </Box>
        </Box>
        <Box textAlign="right" sx={{ fontSize: "0.68rem", lineHeight: 1.4 }}>
          <Box><Box component="span" sx={{ color: accent, fontWeight: 700, fontFamily: MONO }}>{totalActive}</Box> <Box component="span" sx={{ color: muted }}>active</Box></Box>
          {totalInactive > 0 && (
            <Box><Box component="span" sx={{ fontWeight: 700, fontFamily: MONO }}>{totalInactive}</Box> <Box component="span" sx={{ color: muted }}>inactive</Box></Box>
          )}
        </Box>
      </Box>

      {/* Identification: plane icons tinted like the map markers */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(5, 1fr)"
        gap={0.5}
        py={0.75}
        sx={{ borderTop: `1px solid ${alpha("#fff", 0.12)}`, borderBottom: `1px solid ${alpha("#fff", 0.12)}` }}
      >
        {foeItems.map(({ key, label, code }) => (
          <Tooltip key={key} title={label} placement="top" arrow>
            <Box display="flex" flexDirection="column" alignItems="center" gap={0.25}>
              <img
                src="/assets/plane.svg"
                alt={label}
                style={{
                  width: 20,
                  height: 20,
                  filter: `
                    brightness(0)
                    saturate(100%)
                    ${getColorFilter(code, 0, foeFilters)}
                  `,
                }}
              />
              <Box sx={{ fontFamily: MONO, fontWeight: 700, fontSize: "0.78rem", fontVariantNumeric: "tabular-nums" }}>
                {foeSummary[key]}
              </Box>
            </Box>
          </Tooltip>
        ))}

        <Tooltip title="Important" placement="top" arrow>
          <Box display="flex" flexDirection="column" alignItems="center" gap={0.25}>
            {/* Plane inside a red ring: how an important target looks on the map */}
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                border: `2px solid ${IMPORTANT_RED}`,
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: foeSummary.important >= 1 ? "blinkBorder 0.8s infinite" : "none",
                "@keyframes blinkBorder": {
                  "0%, 100%": { boxShadow: `0 0 0 ${IMPORTANT_RED}` },
                  "50%": { boxShadow: `0 0 10px ${IMPORTANT_RED}` },
                },
              }}
            >
              <img
                src="/assets/plane.svg"
                alt="Important"
                style={{ width: 13, height: 13, filter: "brightness(0) invert(1)" }}
              />
            </Box>
            <Box
              sx={{
                fontFamily: MONO,
                fontWeight: 700,
                fontSize: "0.78rem",
                color: foeSummary.important >= 1 ? IMPORTANT_RED : "#fff",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {foeSummary.important}
            </Box>
          </Box>
        </Tooltip>
      </Box>

      {/* Per outpost */}
      <Box mt={0.75}>
        <Box display="grid" gridTemplateColumns={OUTPOST_COLUMNS} alignItems="center" mb={0.25}>
          {sectionLabel("OUTPOST")}
          {sectionLabel("TOTAL", true)}
          {sectionLabel("ACT", true)}
          {sectionLabel("INACT", true)}
        </Box>
        <Box
          sx={{
            maxHeight: 132,
            overflowY: "auto",
            pr: 0.25,
            scrollbarWidth: "thin",
            scrollbarColor: `${alpha("#fff", 0.25)} transparent`,
          }}
        >
          {outposts.map(([num, { total, active, inactive }]) => (
            <Box
              key={num}
              display="grid"
              gridTemplateColumns={OUTPOST_COLUMNS}
              alignItems="center"
              sx={{
                py: 0.2,
                fontFamily: MONO,
                fontVariantNumeric: "tabular-nums",
                borderBottom: `1px solid ${alpha("#fff", 0.06)}`,
                "&:last-of-type": { borderBottom: "none" },
              }}
            >
              <Box sx={{ color: alpha("#fff", 0.85) }}>{num}</Box>
              <Box textAlign="right" sx={{ fontWeight: 700, color: "#fff" }}>{total}</Box>
              <Box textAlign="right" sx={{ fontWeight: 700, color: active > 0 ? accent : muted }}>{active}</Box>
              <Box textAlign="right" sx={{ color: inactive > 0 ? alpha("#fff", 0.85) : muted }}>{inactive}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TargetsState;
